/**
 * Quest Recommendation screen.
 *
 * Layout (matches the mockup):
 *
 *  - Domain name + icon at the top.
 *  - Hero card for the ``best_fit`` quest: large icon, title,
 *    whyItMatters, "Builds toward" preview (first 2 milestones),
 *    concept chips, ETA, primary CTA "Start this quest".
 *  - Two compact alternative cards (the ``alternative`` quests),
 *    each with an "Explore" button.
 *  - "Explore all quests in this domain" link at the bottom that
 *    routes to the Explore Quests screen.
 *
 * The intro paragraph (1-2 sentences, PSI-personal) is rendered
 * above the screen by the parent ``ArcGenerationSession`` shell
 * — this component is just the cards.
 */

import React from "react";
import { css } from "@emotion/css";
import type {
  ArcQuestRecommendation,
  ArcQuestCategory,
  ArcMilestone,
} from "../../types/arcGenerationSession";
import { useDomainCatalog } from "../../data/domainCatalog";
import { questIconSvg } from "../../data/questIcons";
import { MathText } from "../../components/MathMarkdown";

// ── Helpers ─────────────────────────────────────────────────────────

function parseChips(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** First domainId from a comma-separated list. */
function primaryDomainId(domains: string | undefined): string | null {
  const list = parseChips(domains);
  return list.length ? list[0] : null;
}

// ── Styles ──────────────────────────────────────────────────────────

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const domainHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
  & > .icon {
    width: 28px;
    height: 28px;
    color: var(--color-primary);
    & > svg { width: 100%; height: 100%; display: block; }
  }
  & > .name {
    font-size: 0.95em;
    font-weight: 600;
    color: var(--color-primary);
  }
`;

const heroCardStyle = css`
  position: relative;
  border-radius: 18px;
  background: var(--color-coach-bubble-bg);
  border: 1px solid var(--color-primary-border);
  padding: 22px 22px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* Subtle primary glow so the recommended card visibly leads. */
  box-shadow: 0 8px 28px var(--color-primary-glow);
`;

const recommendedBadgeStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--color-success-bg);
  color: var(--color-success-text);
`;

const heroHeaderRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const heroIconBoxStyle = css`
  width: 76px;
  height: 76px;
  flex-shrink: 0;
  border-radius: 16px;
  background: var(--color-primary-muted);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  & > svg { width: 100%; height: 100%; display: block; }
`;

const heroTitleStyle = css`
  font-size: 1.4em;
  font-weight: 700;
  line-height: 1.2;
  color: var(--color-text);
`;

const heroWhyStyle = css`
  font-size: 0.95em;
  color: var(--color-text-secondary);
  line-height: 1.55;
  margin-top: 6px;
`;

const buildsTowardWrapStyle = css`
  border-top: 1px solid var(--color-border-subtle);
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const buildsTowardLabelStyle = css`
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--color-text-muted);
  text-transform: uppercase;
`;

const buildsTowardRowStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const milestonePreviewStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 10px;
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border-subtle);
  & > .icon {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    color: var(--color-primary);
    & > svg { width: 100%; height: 100%; display: block; }
  }
  & > .text {
    font-size: 0.84em;
    color: var(--color-text);
    line-height: 1.3;
  }
`;

const conceptChipsRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const conceptChipStyle = css`
  font-size: 0.82em;
  padding: 4px 11px;
  border-radius: 999px;
  background: var(--color-bg-raised);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-subtle);
`;

const etaRowStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85em;
  color: var(--color-text-muted);
  & .dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--color-text-muted);
  }
`;

const heroCtaStyle = css`
  width: 100%;
  padding: 14px 18px;
  border: none;
  border-radius: 12px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 1em;
  font-weight: 700;
  cursor: pointer;
  margin-top: 4px;
  transition: filter 0.15s ease, transform 0.05s ease;
  &:hover:not(:disabled) { filter: brightness(1.05); }
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const altCardStyle = css`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  & > .icon {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    border-radius: 12px;
    background: var(--color-primary-muted);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    & > svg { width: 100%; height: 100%; display: block; }
  }
  & > .text {
    flex: 1;
    min-width: 0;
    & > .title {
      font-size: 0.95em;
      font-weight: 700;
      line-height: 1.25;
      color: var(--color-text);
    }
    & > .why {
      font-size: 0.85em;
      color: var(--color-text-secondary);
      margin-top: 4px;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
`;

const altExploreBtnStyle = css`
  flex-shrink: 0;
  padding: 8px 14px;
  border-radius: 999px;
  background: transparent;
  border: 1px solid var(--color-primary);
  color: var(--color-primary);
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--color-primary-muted);
  }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

// Shimmering placeholder shown in place of the "Explore" button
// while the rest of the map is still generating — a disabled
// button reads as broken on mobile (tap does nothing), whereas a
// shimmer clearly signals "loading, not yet".  Sized identically
// to the real button: same padding/border/font, with a hidden
// "Explore" ghost label so the box matches exactly.
const exploreShimmerStyle = css`
  flex-shrink: 0;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid transparent;
  background: var(--color-bg-raised);
  position: relative;
  overflow: hidden;
  & > .ghost {
    font-size: 0.85em;
    font-weight: 600;
    visibility: hidden;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.09) 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    animation: alt-explore-shimmer 1.4s ease-in-out infinite;
  }
  @keyframes alt-explore-shimmer {
    to { transform: translateX(100%); }
  }
`;

const exploreAllLinkStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  background: transparent;
  border: none;
  color: var(--color-primary);
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const generatingMapStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px;
  font-size: 0.9em;
  color: var(--color-text-muted);
`;

const generatingSpinnerStyle = css`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  border-top-color: var(--color-primary);
  animation: qr-spin 0.7s linear infinite;
  @keyframes qr-spin {
    to { transform: rotate(360deg); }
  }
`;

// Fixed-width slot for the animated "…" so the centered label
// doesn't jitter as the dot count cycles 0 → 3.
const generatingDotsStyle = css`
  display: inline-block;
  width: 1.4ch;
  text-align: left;
`;

// Subtle "strong alternative" marker on the alternative cards —
// echoes the hero badge's pill shape but in a quieter primary
// tint, so it reads as a recommendation without competing with
// the hero's success-green badge.
const strongAltBadgeStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.62em;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--color-primary-muted);
  color: var(--color-primary);
  margin-bottom: 5px;
  & > .diamond {
    font-size: 0.85em;
    line-height: 1;
  }
`;

/**
 * The bottom-of-screen progress indicator while the recommendation
 * is still streaming.  Two phases:
 *  - "alternatives": the hero is in, the alternative cards aren't
 *    yet — animated "Generating alternative recommendations…" with
 *    the dot count cycling 0 → 3.
 *  - "map": alternatives are shown, the rest of the comprehensive
 *    map is still generating — alternates between
 *    "Generating the rest of your map…" and a reassuring
 *    "This can take a couple minutes" every few seconds.
 */
const GeneratingIndicator: React.FC<{ phase: "alternatives" | "map" }> = ({
  phase,
}) => {
  const [dots, setDots] = React.useState(0);
  const [mapMsg, setMapMsg] = React.useState(0);

  React.useEffect(() => {
    if (phase !== "alternatives") return;
    setDots(0);
    const id = window.setInterval(() => setDots((d) => (d + 1) % 4), 450);
    return () => window.clearInterval(id);
  }, [phase]);

  React.useEffect(() => {
    if (phase !== "map") return;
    setMapMsg(0);
    // 4.5s per message — long enough to read each comfortably.
    const id = window.setInterval(() => setMapMsg((m) => (m + 1) % 2), 4500);
    return () => window.clearInterval(id);
  }, [phase]);

  return (
    <div className={generatingMapStyle} data-testid="quest-map-generating">
      <span className={generatingSpinnerStyle} aria-hidden="true" />
      {phase === "alternatives" ? (
        <span>
          Generating alternative recommendations
          <span className={generatingDotsStyle}>{".".repeat(dots)}</span>
        </span>
      ) : (
        <span>
          {mapMsg === 0
            ? "Generating the rest of your map…"
            : "This can take a couple minutes"}
        </span>
      )}
    </div>
  );
};

// ── Component ───────────────────────────────────────────────────────

interface QuestRecommendationProps {
  // The best_fit quest, in its own field so the hero card can
  // render before any alternative streams.  Has one entry once
  // streamed.
  heroQuest: ArcQuestRecommendation[];
  // Every other quest (the 2 alternatives + the rest).
  quests: ArcQuestRecommendation[];
  categories: ArcQuestCategory[];
  milestones: ArcMilestone[];
  // True while the quest_recommendation envelope is still
  // streaming.  The screen renders progressively — hero +
  // alternatives appear as they arrive — so this only gates the
  // map-dependent affordances (Explore) and the bottom "still
  // generating" indicator.
  isStreaming: boolean;
  // True once the best_fit quest's milestones have fully streamed
  // and been checkpoint-saved on the backend.  Until then the
  // hero's "Start this quest" CTA stays disabled.
  heroReady: boolean;
  // True from the moment the learner clicks "Start this quest"
  // until the session completes — drives the CTA affirmation.
  submittingQuest: boolean;
  onChooseQuest: (questId: string) => void;
  onExploreQuest: (questId: string) => void;
  onExploreAll: () => void;
}

export const QuestRecommendation: React.FC<QuestRecommendationProps> = ({
  heroQuest,
  quests,
  milestones,
  isStreaming,
  heroReady,
  submittingQuest,
  onChooseQuest,
  onExploreQuest,
  onExploreAll,
}) => {
  const { domains: domainsById } = useDomainCatalog();

  // A quest item is renderable once its title has begun streaming —
  // a card with an empty title would flash blank.
  const isRenderable = (q: ArcQuestRecommendation) => !!q.title;

  // The hero comes from its own ``heroQuest`` field, which streams
  // before the alternatives — so the hero card never waits on them.
  const best = heroQuest.find(isRenderable);
  const alternatives = quests
    .filter((q) => q.recommendationTier === "alternative" && isRenderable(q))
    .slice(0, 2);

  // Group milestones by questId, sorted by ``order`` (1-indexed
  // numeric string).  The frontend owns the grouping because
  // milestones stream as a flat list (see comment on the schema).
  const milestonesByQuest = React.useMemo(() => {
    const out: Record<string, ArcMilestone[]> = {};
    for (const m of milestones ?? []) {
      const qid = m.questId;
      if (!qid) continue;
      (out[qid] ??= []).push(m);
    }
    for (const list of Object.values(out)) {
      list.sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
    }
    return out;
  }, [milestones]);

  const bestMilestones = best ? (milestonesByQuest[best.questId] ?? []) : [];

  const primaryDomain = best ? primaryDomainId(best.domains) : null;
  const domainName = primaryDomain ? domainsById[primaryDomain]?.name : null;

  return (
    <div className={containerStyle} data-testid="quest-recommendation">
      {/* Domain context strip */}
      {primaryDomain && domainName && (
        <div className={domainHeaderStyle}>
          <span
            className="icon"
            aria-hidden="true"
            dangerouslySetInnerHTML={{
              __html: domainsById[primaryDomain]?.iconSvg ?? "",
            }}
          />
          <span className="name">{domainName}</span>
        </div>
      )}

      {/* Hero card — best_fit quest.  Appears as soon as the
          best_fit quest streams in; its "Start" CTA stays disabled
          until ``heroReady``. */}
      {best && (
        <HeroQuestCard
          quest={best}
          milestones={bestMilestones}
          heroReady={heroReady}
          submittingQuest={submittingQuest}
          onChoose={() => onChooseQuest(best.questId)}
        />
      )}

      {/* Alternative cards — appear as each streams in */}
      {alternatives.map((alt) => (
        <AlternativeQuestCard
          key={alt.questId}
          quest={alt}
          isStreaming={isStreaming || submittingQuest}
          onExplore={() => onExploreQuest(alt.questId)}
        />
      ))}

      {/* Bottom region: while the rest of the map is still
          generating, show a lightweight indicator — phased so it
          first speaks to the alternatives, then the full map.
          Once it's done, swap in the "Explore all quests" entry. */}
      {isStreaming ? (
        <GeneratingIndicator
          phase={
            alternatives.length >= 2 ||
            quests.some(
              (q) => isRenderable(q) && q.recommendationTier !== "alternative",
            )
              ? "map"
              : "alternatives"
          }
        />
      ) : (
        <button
          type="button"
          className={exploreAllLinkStyle}
          onClick={onExploreAll}
          disabled={submittingQuest}
          data-testid="explore-all-quests"
        >
          Explore all quests in this domain →
        </button>
      )}
    </div>
  );
};

// ── Hero card ────────────────────────────────────────────────────────

const HeroQuestCard: React.FC<{
  quest: ArcQuestRecommendation;
  milestones: ArcMilestone[];
  heroReady: boolean;
  submittingQuest: boolean;
  onChoose: () => void;
}> = ({ quest, milestones, heroReady, submittingQuest, onChoose }) => {
  const concepts = parseChips(quest.coreConcepts);
  const milestonesPreview = milestones.slice(0, 2);
  // CTA copy tracks the three states: starting (clicked), ready
  // (startable while the rest of the map keeps generating), and
  // preparing (hero milestones still streaming).
  const ctaLabel = submittingQuest
    ? "Starting your quest…"
    : heroReady
      ? "Start this quest →"
      : "Preparing your quest…";
  return (
    <div
      className={heroCardStyle}
      data-testid={`quest-hero-${quest.questId}`}
    >
      <span className={recommendedBadgeStyle}>
        ★ Recommended for you
      </span>

      <div className={heroHeaderRowStyle}>
        <span
          className={heroIconBoxStyle}
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: questIconSvg(quest.iconKey) }}
        />
        <div>
          <div className={heroTitleStyle}>
            <MathText text={quest.title} />
          </div>
          <div className={heroWhyStyle}>
            <MathText text={quest.whyItMatters} />
          </div>
        </div>
      </div>

      {milestonesPreview.length > 0 && (
        <div className={buildsTowardWrapStyle}>
          <span className={buildsTowardLabelStyle}>Builds toward</span>
          <div className={buildsTowardRowStyle}>
            {milestonesPreview.map((m: ArcMilestone) => (
              <div key={m.milestoneId} className={milestonePreviewStyle}>
                <span
                  className="icon"
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{ __html: questIconSvg(m.iconKey) }}
                />
                <span className="text">{m.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {concepts.length > 0 && (
        <div
          className={conceptChipsRowStyle}
          data-testid="quest-hero-concepts"
        >
          {concepts.map((c) => (
            <span key={c} className={conceptChipStyle}>{c}</span>
          ))}
        </div>
      )}

      <div className={etaRowStyle}>
        <span>⏱</span>
        <span>{quest.etaSessions} sessions</span>
        <span className="dot" />
        <span>{quest.etaMinutesPerSession} min each</span>
      </div>

      <button
        type="button"
        className={heroCtaStyle}
        onClick={onChoose}
        disabled={!heroReady || submittingQuest}
        data-testid="quest-hero-start"
      >
        {ctaLabel}
      </button>
    </div>
  );
};

// ── Alternative card ────────────────────────────────────────────────

const AlternativeQuestCard: React.FC<{
  quest: ArcQuestRecommendation;
  isStreaming: boolean;
  onExplore: () => void;
}> = ({ quest, isStreaming, onExplore }) => {
  // Use the first sentence of whyItMatters as the snippet on the
  // compact card; strip markdown bold markers for the preview.
  const snippet = (() => {
    const first = (quest.whyItMatters || "").split(/(?<=[.!?])\s/)[0] || "";
    return first.replace(/\*\*([^*]+)\*\*/g, "$1");
  })();
  return (
    <div
      className={altCardStyle}
      data-testid={`quest-alt-${quest.questId}`}
    >
      <span
        className="icon"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: questIconSvg(quest.iconKey) }}
      />
      <div className="text">
        <span className={strongAltBadgeStyle}>
          <span className="diamond" aria-hidden="true">◆</span>
          Strong alternative
        </span>
        <div className="title">{quest.title}</div>
        <div className="why">{snippet}</div>
      </div>
      {/* "Explore" opens the full-map overlay, which only exists
          once the whole map has streamed.  Until then, show a
          shimmering placeholder rather than a dead disabled button
          — a tap-that-does-nothing reads as broken on mobile. */}
      {isStreaming ? (
        <div
          className={exploreShimmerStyle}
          aria-hidden="true"
          data-testid={`quest-alt-explore-loading-${quest.questId}`}
        >
          <span className="ghost">Explore</span>
        </div>
      ) : (
        <button
          type="button"
          className={altExploreBtnStyle}
          onClick={onExplore}
          data-testid={`quest-alt-explore-${quest.questId}`}
        >
          Explore
        </button>
      )}
    </div>
  );
};
